<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { useInfiniteScroll } from '@vueuse/core'
import { v4 as uuid } from 'uuid'
import { tagsConfig as config } from './configs/vowpath'
import TagsSelector from './components/TagsSelector.vue'
import { api } from '@/api'

// 定义主题变量供 CSS v-bind 使用
const filterBgColor = computed(() => config.theme.filterBgColor)
const filterActiveBgColor = computed(() => config.theme.filterActiveBgColor)
const filterColor = computed(() => config.theme.filterColor)
const filterActiveColor = computed(() => config.theme.filterActiveColor)

const route = useRoute()
const loading = ref(true)
const tabLoading = ref(false)
const router = useRouter()
const tagsList = ref<Array<{ id: string; isHot: boolean; resourceList: object; tagName: string }>>([])
const activeTab = ref(0)
const contentTabs = [config.texts.mostPopular, config.texts.newArrival]
const activeContentTab = ref(0)
const booksData = ref<any[]>([])

const page = ref(1)
const total = ref<number>(0)
const fetching = ref(false)
const ending = ref(false)
const error = ref<any>()
const pagination = computed(() => ({
  pageIndex: page.value,
  pageSize: 20,
}))

const filteredBooks = computed(() => {
  return booksData.value.filter((book) => {
    const selectedContentTab = contentTabs[activeContentTab.value]
    return book.contentType === selectedContentTab
  })
})

onMounted(async () => {
  loading.value = true
  try {
    await getAllTags()
    const tagId = route.query.tagId as string
    if (tagId) {
      const index = tagsList.value.findIndex(tag => tag.id === tagId)
      if (index !== -1) {
        activeTab.value = index
        await handleTabChange(index)
      }
    }
    else {
      await fetchBooksByTag()
    }
  }
  finally {
    loading.value = false
  }
})

watch([activeTab, activeContentTab], async () => {
  tabLoading.value = true
  resetScrollPosition()
  booksData.value = []
  page.value = 1
  ending.value = false
  fetching.value = false
  await fetchBooksByTag()
  tabLoading.value = false
})

function gotoBook(id: string) {
  router.push({
    name: 'book',
    params: { contentId: id },
  })
}

async function getAllTags() {
  try {
    loading.value = true
    const { data } = await api.get('/tag/getAllTag')
    tagsList.value = data
  }
  catch (error) {
    console.error('获取标签失败:', error)
  }
  finally {
    loading.value = false
  }
}

async function handleTabChange(index: number) {
  if (activeTab.value === index)
    return
  resetScrollPosition()
  booksData.value = []
  page.value = 1
  ending.value = false
  fetching.value = false

  activeTab.value = index
  await fetchBooksByTag()
}
async function fetchBooksByTag() {
  if (fetching.value || ending.value)
    return
  fetching.value = true

  if (tagsList.value.length === 0) {
    fetching.value = false
    return
  }

  try {
    const tagId = tagsList.value[activeTab.value].id
    const orderBy = activeContentTab.value + 1
    const { data } = await api.get('/content/getTagContentList', {
      params: {
        tagId,
        orderBy,
        pageIndex: page.value,
        pageSize: pagination.value.pageSize,
      },
    })

    const newBooks = data.records.map((item: any) => ({
      id: item.id,
      contentName: item.contentName,
      contentCoverUrl: item.contentCoverUrl,
      introduce: item.introduce,
      tags: item.tagList || [],
      contentType: orderBy === 1 ? config.texts.mostPopular : config.texts.newArrival,
      viewUsers: item.viewUsers,
      contentClassName: item.contentClassName,
    }))

    booksData.value = [...booksData.value, ...newBooks]
    total.value = data.total

    if (page.value >= data.pages)
      ending.value = true
    else
      page.value += 1
  }
  catch (err) {
    console.error('获取书籍失败:', err)
    error.value = err
  }
  finally {
    fetching.value = false
  }
}

const contentContainer = ref<HTMLElement>()
const infiniteScrollProps = useInfiniteScroll(
  contentContainer,
  async () => {
    if (fetching.value || ending.value || error.value)
      return
    await fetchBooksByTag()
  },
  { distance: 50 },
)
function resetScrollPosition() {
  contentContainer.value?.scrollTo({ top: 0 })
}
</script>

<template>
  <div v-if="loading" class="py-120px flex items-center justify-center space-x-4 text-13px text-n-text-3">
    <div class="animate-spin">
      <i-icon-park-outline-loading />
    </div>
    <p>{{ config.texts.loading }}</p>
  </div>
  <div v-else id="tags" class="h-100dvh bg-n-bg-global overflow-scroll relative">
    <BasicNavbar :scale="true" class="color-n-text-2">
      <template #default>
        <div class="absolute inset-0 flex justify-center items-center pointer-events-none">
          <p class="text-20px text-n-text-1">
            {{ config.texts.allTags }}
          </p>
        </div>
      </template>
    </BasicNavbar>

    <TagsSelector :active-tab="activeTab" :tags-list="tagsList" @update:active-tab="(newVal) => activeTab = newVal" />
    <!-- 内容区域 -->
    <div id="tagsContainer" class="h-[calc(100dvh-120px)] px-4 mb-20px mt-40px">
      <div class="relative bg-n-bg-card h-full flex flex-col rounded-8px">
        <div class="top-0 z-9 bg-n-bg-card w-full my-4">
          <div class="relative mx-2 h-8 filter-container rounded-18px">
            <div class="absolute inset-0 flex">
              <button
                v-for="(tab, index) in contentTabs" :key="index"
                class="filter-tab flex-1 h-full text-sm font-medium rounded-[18px]" :class="{
                  'filter-tab--active': activeContentTab === index,
                  'filter-tab--inactive': activeContentTab !== index,
                }" @click="activeContentTab = index"
              >
                {{ tab }}
              </button>
            </div>
          </div>
        </div>
        <!-- 书籍区域 -->
        <div ref="contentContainer" v-bind="infiniteScrollProps" class="flex-1 overflow-y-auto">
          <!-- Tab切换Loading -->
          <div v-if="tabLoading" class="py-120px flex items-center justify-center space-x-4 text-13px text-n-text-3">
            <div class="animate-spin">
              <i-icon-park-outline-loading />
            </div>
            <p>{{ config.texts.loading }}</p>
          </div>

          <template v-else>
            <div class="space-y-4">
              <div
                v-for="(item, index) in filteredBooks" :key="index" class="flex items-center space-x-15px px-3"
                @click="gotoBook(item.id)"
              >
                <BizBookCover
                  :id="uuid()"
                  :src="item.contentCoverUrl || ''"
                  :extra="{
                    author: item?.authorInfo?.author,
                    module_name: 'tags',
                    ...item,
                  }"
                  class="w-18 rounded-8px"
                />

                <div class="flex-1">
                  <p class="text-15px font-bold line-clamp-1 text-n-text-1">
                    {{ item.contentName }}
                  </p>
                  <p class="text-13px mt-10px line-clamp-2 text-n-text-2">
                    {{ item.introduce }}
                  </p>

                  <div class="mt-2 flex items-center">
                    <div class="flex flex-wrap mr-2 flex-1 min-w-0">
                      <span
                        class="inline-flex items-center bg-n-bg-tag rounded-14px text-11px px-6px py-3px whitespace-nowrap text-n-text-2"
                      >
                        {{ item.contentClassName }}
                      </span>
                    </div>
                    <div class="flex-shrink-0 ml-auto pl-2">
                      <div class="flex items-center justify-end text-10px text-n-text-3 w-full">
                        <i-icon-park-outline-preview-open class="w-3 h-3 mr-1 flex-shrink-0" />
                        <span class="whitespace-nowrap">{{ item.viewUsers || 0 }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 没有更多数据提示 -->
            <div v-if="ending && !fetching" class="py-15px text-13px text-n-text-3 text-center">
              {{ config.texts.noMore }}
            </div>
            <!-- 分页加载Loading -->
            <div
              v-if="fetching && booksData.length > 0"
              class="py-15px flex items-center justify-center space-x-4 text-13px text-n-text-3"
            >
              <div class="animate-spin">
                <i-icon-park-outline-loading />
              </div>
              <p>{{ config.texts.loading }}</p>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}

img {
  background-color: var(--b-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--t-quaternary);
  font-size: 10px;
}

.flex-1 {
  flex: 1;
}

.overflow-y-auto {
  overflow-y: auto !important;
  -webkit-overflow-scrolling: touch;
  height: 100%;
}

/* 筛选器样式 */
.filter-container {
  background-color: v-bind(filterBgColor);
}

.filter-tab--active {
  background-color: #F3EAFF;
  color: #BB7AFB;
}

.filter-tab--inactive {
  color: v-bind(filterColor);
}
</style>
